import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { renderEnergyInfrastructureLayer } from "../lib/energyInfrastructureLayer";
import { addGrayscaleBasemapControl } from "../lib/mapBasemap";
import "leaflet/dist/leaflet.css";
import { buildCountryTileUrls } from "../lib/geozoneLayers";
import { COUNTRY_WMS_MAX_NATIVE_ZOOM } from "@client/lib/geozoneZoom";
import {
  defaultGeozoneLayers,
  fetchVectorGeozones,
  renderEd269Layer,
  filterEd269Features,
  filterExpiredEd269Features,
  loadGeozoneShowAll,
  saveGeozoneShowAll,
  ed269WebRestriction,
  ed269WebColors,
  ed269WebAltitude,
  ED269_WEB_LABELS,
} from "../lib/ed269Web";
import { ed269IsTemporary } from "../../../shared/ed269Restriction";
import { DEFAULT_REACHABLE_ALTITUDE_M } from "../../../shared/geozoneAltitudeFilter";
import { fetchElevation } from "@client/lib/elevation";
import { useShowDataSources } from "../lib/dataSource";
import { ed269SourceLabelFor } from "../lib/ed269SourceLabel";
import { useSubscription, entitlementHasCountry } from "../lib/subscription";
import { countryName } from "../../../shared/countryEntitlements";
import { useGermanEnergyInfrastructure } from "../lib/useGermanEnergyInfrastructure";
import {
  getEnergyInfrastructureCountry,
  hasIncompleteEnergyInfrastructureSources,
  type GermanEnergyInfrastructureItem,
} from "@shared/germanEnergyInfrastructure";
import { hasOwnMap } from "../../../shared/countryDetection";

// Eingebettete, eigene Geozonen-Karte für die Flugplanung (StepMap). Zeigt für
// Länder mit eigenen Daten (DE/CH/NL/FR/LU als WMS-Kacheln, AT/BE/DK als
// ED-269-Vektor) das gleiche Geozonen-Overlay wie die große Karte (MapPage) —
// zentriert auf den geplanten Standort, mit Marker. Bewusst fokussiert: kein
// Layer-Umschalter, keine Suche, keine NOTAMs (die liefert der Pre-Check).

const ICON_URL = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png";
const ICON_RETINA = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png";
const SHADOW_URL = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png";
const DefaultIcon = L.icon({
  iconRetinaUrl: ICON_RETINA,
  iconUrl: ICON_URL,
  shadowUrl: SHADOW_URL,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const WMS_MIN_ZOOM = 5;
const WMS_MAX_ZOOM = 18;
const DEFAULT_ZOOM = 12;

function infrastructurePopup(
  item: GermanEnergyInfrastructureItem,
  officialCountry: NonNullable<ReturnType<typeof getEnergyInfrastructureCountry>>,
  countryIso: string,
): HTMLElement {
  const root = document.createElement("div");
  const title = document.createElement("strong");
  title.textContent = item.name;
  root.appendChild(title);

  const info = document.createElement("p");
  const comparisonSource = officialCountry.assetComparisonSourceByKind?.[item.kind] ??
    officialCountry.officialGeozoneSource.label;
  const comparison = item.source === "RIVM" || item.source === "BFE" || item.source === "ACT"
    ? "amtlicher Anlagendatensatz; Flugbeschränkung nicht abgeglichen"
    : item.officialMatch === "not_found"
      ? `nicht in ${comparisonSource} gefunden`
      : item.officialMatch === "unknown"
        ? officialCountry.comparableOfficialAssetKinds.includes(item.kind)
          ? `kein sicherer Abgleich mit ${comparisonSource} möglich`
          : "kein amtlicher Vergleichsdatensatz für diesen Anlagentyp"
        : `in ${comparisonSource} bestätigt`;
  info.textContent =
    `${item.source} · ${comparison} · ${Math.round(item.distanceMeters)} m vom Start · Betreiber: ${item.operatorName || item.operatorMastrNumber || "nicht angegeben"}${item.operatorMastrNumber ? ` · MaStR-Nr. ${item.operatorMastrNumber}` : ""}`;
  root.appendChild(info);

  if (countryIso === "DE") {
    const law = document.createElement("p");
    law.textContent =
      "§ 21h Abs. 3 Nr. 3 LuftVO: Über/innerhalb von 100 m zu zentralen Energieanlagen nur mit ausdrücklicher Zustimmung der zuständigen Stelle oder des Betreibers. Die 100 m messen sich ab der Anlagenbegrenzung; die Kartendistanz ist nur ein Hinweis.";
    root.appendChild(law);
  }

  const contactLinks = document.createElement("p");
  const addLink = (href: string, label: string) => {
    const link = document.createElement("a");
    link.href = href;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = label;
    contactLinks.appendChild(link);
    contactLinks.appendChild(document.createTextNode(" · "));
  };
  if (item.operatorProfileUrl) addLink(item.operatorProfileUrl, "Betreiberprofil im MaStR öffnen");
  if (item.sourceRecordUrl) addLink(item.sourceRecordUrl, "Anlageneintrag öffnen");
  if (item.operatorContact?.phone) {
    addLink(`tel:${item.operatorContact.phone.replace(/[^\d+*#,;]/g, "")}`, "Telefon");
  }
  if (item.operatorContact?.email) {
    addLink(`mailto:${item.operatorContact.email.replace(/^mailto:/i, "")}`, "E-Mail");
  }
  if (item.operatorContact?.website) {
    try {
      const candidate = /^[a-z][a-z\d+.-]*:/i.test(item.operatorContact.website)
        ? item.operatorContact.website
        : `https://${item.operatorContact.website}`;
      const url = new URL(candidate);
      if (url.protocol === "https:" || url.protocol === "http:") addLink(url.href, "Website");
    } catch {
      // Ungültige OSM-Kontakt-URL nicht als Link ausgeben.
    }
  }
  if (contactLinks.childNodes.length) {
    contactLinks.removeChild(contactLinks.lastChild!);
    root.appendChild(contactLinks);
  } else {
    const noContact = document.createElement("p");
    noContact.textContent = "Keine direkten öffentlichen Kontaktdaten in den abgefragten Quellen hinterlegt.";
    root.appendChild(noContact);
  }

  const extra = document.createElement("p");
  if (item.estimatedTipHeightM != null) {
    extra.textContent = `Geschätzte Gesamthöhe: ca. ${Math.round(item.estimatedTipHeightM)} m`;
  } else if (item.taggedHeightM != null) {
    extra.textContent = `In ${item.source} eingetragene Höhe: ${Math.round(item.taggedHeightM)} m`;
  } else if (item.voltage) {
    extra.textContent = `Spannung laut ${item.source}: ${item.voltage}`;
  }
  if (extra.textContent) root.appendChild(extra);
  return root;
}

type Props = {
  latitude: number;
  longitude: number;
  /** ISO-2 des geplanten Landes (für die Vektor-Geozonen AT/BE/DK). */
  countryIso: string;
  /** Vom Nutzer geplante Flughöhe (AGL, in Metern). Bestimmt, welche
   *  Vektor-Geozonen wegen zu hoher Untergrenze ausgeblendet werden.
   *  Fällt auf 120 m (EU Open-Kategorie) zurück, wenn nicht gesetzt. */
  reachableAltitudeM?: number;
};

export default function PlanningGeozoneMap({ latitude, longitude, countryIso, reachableAltitudeM }: Props) {
  const isAdmin = useShowDataSources();
  // Länder-Berechtigung (Task #522): Geozonen erscheinen nur für freigeschaltete
  // Länder (serverseitig erzwungen). Ist das geplante Land gesperrt, zeigen wir
  // statt der leeren Karte einen klaren "Land freischalten"-Hinweis.
  const { data: subscription } = useSubscription();
  const countryLocked =
    !!countryIso && !entitlementHasCountry(subscription?.entitlement, countryIso);
  const hasInfrastructureData = hasOwnMap(countryIso);
  const energyInfrastructureQuery = useGermanEnergyInfrastructure(
    latitude,
    longitude,
    5,
    countryIso,
    hasInfrastructureData && !countryLocked,
  );
  const reachableM =
    typeof reachableAltitudeM === "number" && reachableAltitudeM > 0
      ? reachableAltitudeM
      : DEFAULT_REACHABLE_ALTITUDE_M;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const vectorLayerRef = useRef<L.LayerGroup | null>(null);
  const energyLayerRef = useRef<L.LayerGroup | null>(null);
  // BE: dauerhafte Zonen als serverseitig rasterisierte PNG-Kachel-Ebene (wie
  // WMS); nur temporäre skeyes-Zonen bleiben Vektor (siehe Effekt unten).
  const beTileLayerRef = useRef<L.TileLayer | null>(null);
  // Höhen-Filter für Vektor-Geozonen (AT/BE/DK): Untergrenze über der erreichbaren
  // Höhe (Standard 120 m AGL) -> ausblenden. Toggle wird mit MapPage geteilt.
  const [showAllGeozones, setShowAllGeozones] = useState<boolean>(() => loadGeozoneShowAll());
  const [terrainElevationM, setTerrainElevationM] = useState<number | null>(null);
  const [hiddenAboveCount, setHiddenAboveCount] = useState(0);
  // Isolierte Einzel-Zonen-Ansicht (Task #595): per Popup-Button gesetzt, öffnet
  // ein Overlay mit nur dieser Zone (eigene Leaflet-Karte + Datenpanel).
  const [focusedFeature, setFocusedFeature] = useState<GeoJSON.Feature | null>(null);

  // Geländehöhe am geplanten Standort laden (für MSL->AGL-Umrechnung).
  useEffect(() => {
    if (showAllGeozones) return;
    let cancelled = false;
    fetchElevation(latitude, longitude)
      .then((t) => { if (!cancelled) setTerrainElevationM(t); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [latitude, longitude, showAllGeozones]);

  // Karte einmalig initialisieren.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [latitude, longitude],
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      minZoom: 5,
      maxZoom: 18,
      worldCopyJump: false,
    });
    const baseLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap-Mitwirkende",
      maxZoom: 19,
    }).addTo(map);
    addGrayscaleBasemapControl(map, baseLayer);

    // WMS-Geozonen-Overlay (eine gecachte Kachel-Ebene pro Land mit aktiven
    // Default-Layern — deckt DE/CH/NL/FR/LU ab). Genau wie in MapPage.
    const enabled = defaultGeozoneLayers();
    for (const entry of buildCountryTileUrls(enabled)) {
      const tile = L.tileLayer(entry.url, {
        opacity: 0.7,
        minZoom: WMS_MIN_ZOOM,
        maxZoom: WMS_MAX_ZOOM,
        maxNativeZoom: COUNTRY_WMS_MAX_NATIVE_ZOOM[entry.country] ?? WMS_MAX_ZOOM,
        updateWhenIdle: true,
        updateWhenZooming: false,
        keepBuffer: 1,
      });
      tile.on("tileerror", () => {});
      tile.addTo(map);
    }

    vectorLayerRef.current = L.layerGroup().addTo(map);
    energyLayerRef.current = L.layerGroup().addTo(map);
    markerRef.current = L.marker([latitude, longitude], { icon: DefaultIcon }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      vectorLayerRef.current = null;
      energyLayerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zusätzliche Anlagenquellen als echte Punkt-/Liniengeometrien überlagern.
  useEffect(() => {
    const layer = energyLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!hasInfrastructureData || !energyInfrastructureQuery.data) return;

    renderEnergyInfrastructureLayer(layer, energyInfrastructureQuery.data, setFocusedFeature);
  }, [energyInfrastructureQuery.data, hasInfrastructureData, countryIso]);

  // Auf neue Koordinaten zentrieren + Marker versetzen.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setView([latitude, longitude], DEFAULT_ZOOM);
    if (markerRef.current) markerRef.current.setLatLng([latitude, longitude]);
  }, [latitude, longitude]);

  // ED-269-Vektor-Geozonen (AT/BE/DK) — nur für das geplante Land laden.
  useEffect(() => {
    const map = mapRef.current;
    const layer = vectorLayerRef.current;
    if (!layer || !map) return;
    layer.clearLayers();
    const iso = (countryIso || "").toLowerCase();
    // BE: dauerhafte Zonen als serverseitig rasterisierte PNG-Kachel-Ebene (wie
    // WMS, robust statt Vektor-Polygonen). Bei anderem Land die Kachel entfernen.
    if (iso === "be") {
      if (!beTileLayerRef.current) {
        const tile = L.tileLayer("/api/proxy/geozone-vector-tiles/be/{z}/{x}/{y}.png", {
          opacity: 0.7,
          minZoom: WMS_MIN_ZOOM,
          maxZoom: WMS_MAX_ZOOM,
          updateWhenIdle: true,
          updateWhenZooming: false,
          keepBuffer: 1,
        });
        tile.on("tileerror", () => {});
        tile.addTo(map);
        beTileLayerRef.current = tile;
      }
    } else if (beTileLayerRef.current) {
      map.removeLayer(beTileLayerRef.current);
      beTileLayerRef.current = null;
    }
    if (iso !== "at" && iso !== "be" && iso !== "dk") return;
    const sourceLabel = ed269SourceLabelFor(iso, isAdmin);
    let cancelled = false;
    fetchVectorGeozones(iso as "at" | "be" | "dk")
      .then((features) => {
        if (cancelled || !vectorLayerRef.current) return;
        const notHidden = filterExpiredEd269Features(
          features.filter(
            (f) =>
              f &&
              f.geometry &&
              (f.properties as any)?.hidden !== true &&
              // BE: nur temporäre Zonen als Vektor; dauerhafte zeichnet die Kachel.
              // Kanonische Regel (shared ed269IsTemporary): Start ODER Ende gesetzt.
              (iso !== "be" ||
                ed269IsTemporary((f.properties || {}) as Record<string, any>)),
          ),
        );
        let visible = notHidden;
        let hiddenAbove = 0;
        if (!showAllGeozones) {
          const part = filterEd269Features(notHidden, reachableM, terrainElevationM);
          visible = part.visible;
          hiddenAbove = part.hiddenAbove.length;
        }
        setHiddenAboveCount(hiddenAbove);
        if (visible.length === 0) return;
        renderEd269Layer(vectorLayerRef.current, visible, sourceLabel, setFocusedFeature);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [countryIso, latitude, longitude, showAllGeozones, terrainElevationM, reachableM, isAdmin]);

  return (
    <div className="space-y-2">
      {countryLocked ? (
        <div
          data-testid="hint-locked-country"
          className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span>
            {countryName(countryIso)} ist nicht freigeschaltet. Die Geozonen
            erscheinen erst nach dem Freischalten dieses Landes.
          </span>
        </div>
      ) : null}
      <div
        ref={containerRef}
        className="w-full h-[60vh] min-h-[420px] rounded-lg overflow-hidden border border-slate-200"
      />
      {hasInfrastructureData && energyInfrastructureQuery.error && !energyInfrastructureQuery.data ? (
        <p className="text-xs text-amber-700">
          Zusätzliche öffentliche Anlagengeometrien konnten nicht geladen werden; daraus lässt sich nicht auf Anlagenfreiheit schließen.
        </p>
      ) : null}
      {hasInfrastructureData && energyInfrastructureQuery.error && energyInfrastructureQuery.data ? (
        <p className="text-xs text-amber-700">
          Die Zusatzquellen konnten nicht aktualisiert werden. Zuletzt geladene Treffer bleiben sichtbar; ein fehlender Treffer ist nicht verlässlich.
        </p>
      ) : null}
      {hasInfrastructureData && energyInfrastructureQuery.data &&
      hasIncompleteEnergyInfrastructureSources(energyInfrastructureQuery.data) ? (
        <p className="text-xs text-amber-700">
          Teilergebnis: Mindestens eine relevante Zusatzquelle ist veraltet, unvollständig oder nicht erreichbar. 0 Treffer belegen keine Anlagenfreiheit.
        </p>
      ) : null}
      {hasInfrastructureData && energyInfrastructureQuery.data?.features.length ? (
        <p className="text-xs text-slate-600">
          Quelle und Anlagendetails sind durch Antippen verfügbar.
        </p>
      ) : null}
      {!showAllGeozones && hiddenAboveCount > 0 ? (
        <div className="text-xs text-slate-600">
          {hiddenAboveCount} {hiddenAboveCount === 1 ? "Zone" : "Zonen"} oberhalb deiner geplanten Höhe ({reachableM} m) ausgeblendet.{" "}
          <button
            type="button"
            data-testid="button-show-all-geozones"
            className="underline text-blue-600"
            onClick={() => { setShowAllGeozones(true); saveGeozoneShowAll(true); }}
          >
            Alle Geozonen anzeigen
          </button>
        </div>
      ) : null}
      {showAllGeozones ? (
        <div className="text-xs text-slate-600">
          Alle Geozonen werden angezeigt (auch über {reachableM} m).{" "}
          <button
            type="button"
            data-testid="button-hide-above-geozones"
            className="underline text-blue-600"
            onClick={() => { setShowAllGeozones(false); saveGeozoneShowAll(false); }}
          >
            Zonen oberhalb der Höhe ausblenden
          </button>
        </div>
      ) : null}
      {focusedFeature ? (
        <FocusedZoneModal
          feature={focusedFeature}
          latitude={latitude}
          longitude={longitude}
          countryIso={countryIso}
          isAdmin={isAdmin}
          onClose={() => setFocusedFeature(null)}
        />
      ) : null}
    </div>
  );
}

// Isolierte Einzel-Zonen-Ansicht: eigenes Overlay mit eigener Leaflet-Karte, die
// NUR die gewählte Zone (hervorgehobenes Polygon inkl. Donut-Löcher) plus den
// geplanten Standort-Marker als Kontext zeigt, dazu ein Datenpanel
// (Name/Typ, Beschränkung, Höhe, Details, Antrag, Links). Bewusst ohne
// Radius-Kreis: die Web-Planung führt keinen Radius (siehe Task-Drift).
function FocusedZoneModal({
  feature,
  latitude,
  longitude,
  countryIso,
  isAdmin,
  onClose,
}: {
  feature: GeoJSON.Feature;
  latitude: number;
  longitude: number;
  countryIso: string;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const focusContainerRef = useRef<HTMLDivElement | null>(null);
  const p = (feature.properties || {}) as Record<string, any>;
  const restriction = ed269WebRestriction(p);
  const restrictionLabel = ED269_WEB_LABELS[restriction] || restriction || "Beschränkung";
  const name = (p.name || p.identifier || p.zoneId || "Geozone").toString();
  const altitude = ed269WebAltitude(p);
  const detail = (p.message || p.reason || "").toString().trim();
  const appText = (p.applicationText || "").toString().trim();
  const sourceLabel = isAdmin ? ed269SourceLabelFor((countryIso || "").toLowerCase(), isAdmin) : "";
  const links = [
    { label: (p.applicationUrlLabel || "Antrag stellen").toString(), url: (p.applicationUrl || "").toString() },
    { label: (p.infoUrlLabel || "Weitere Infos").toString(), url: (p.infoUrl || "").toString() },
    { label: (p.contactEmail || "").toString(), url: (p.contactEmail || "").toString() },
  ].filter((l) => l.url.trim() && l.label.trim());

  useEffect(() => {
    if (!focusContainerRef.current) return;
    const map = L.map(focusContainerRef.current, {
      zoomControl: true,
      minZoom: 4,
      maxZoom: 18,
      worldCopyJump: false,
    });
    const baseLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap-Mitwirkende",
      maxZoom: 19,
    }).addTo(map);
    addGrayscaleBasemapControl(map, baseLayer);
    const c = ed269WebColors(restriction);
    const gj = L.geoJSON(feature as any, {
      style: { color: c.color, weight: 2, fillColor: c.fill, fillOpacity: 0.25 },
    }).addTo(map);
    L.marker([latitude, longitude], { icon: DefaultIcon }).addTo(map);
    try {
      const b = gj.getBounds();
      const bounds = b.isValid() ? b.extend([latitude, longitude]) : null;
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      } else {
        map.setView([latitude, longitude], 13);
      }
    } catch {
      map.setView([latitude, longitude], 13);
    }
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => {
      clearTimeout(t);
      map.remove();
    };
  }, [feature, latitude, longitude, restriction]);

  return (
    <div
      data-testid="overlay-zone-focus"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h3 className="pr-2 text-base font-semibold text-slate-900" data-testid="text-zone-focus-name">
            {name}
          </h3>
          <button
            type="button"
            data-testid="button-zone-focus-close"
            className="rounded-md px-2 py-1 text-sm font-medium text-slate-600 hover:bg-slate-100"
            onClick={onClose}
          >
            Schließen
          </button>
        </div>
        <div
          ref={focusContainerRef}
          className="h-[45vh] min-h-[280px] w-full border-b border-slate-200"
        />
        <div className="space-y-2 overflow-y-auto px-4 py-3 text-sm">
          {sourceLabel ? (
            <div className="text-xs text-slate-500">{sourceLabel}</div>
          ) : null}
          <div>
            <span className="font-semibold">Beschränkung:</span> {restrictionLabel}
          </div>
          <div>
            <span className="font-semibold">Höhe:</span> {altitude}
          </div>
          {detail ? (
            <div className="whitespace-pre-line text-slate-700">{detail}</div>
          ) : null}
          {appText ? (
            <div className="border-t border-slate-100 pt-2 text-slate-700">{appText}</div>
          ) : null}
          {links.length > 0 ? (
            <div className="flex flex-col gap-1 pt-1">
              {links.map((l, i) => {
                const isMail = /@/.test(l.url) && !/^https?:/i.test(l.url);
                const href = isMail ? `mailto:${l.url}` : l.url;
                return (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    {l.label}
                  </a>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
